import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Captions, Check, ChevronDown, X } from "lucide-react";
import { Api } from "../lib/api.js";
import { hlsSourceUrl } from "./AudioTrackPicker.js";

/**
 * Subtitle picker for the Watch page. Looks up subtitles by IMDb id
 * (extracted from rd: items or media_items.tvg_id), shows a ranked list,
 * remembers the selected file_id in localStorage per item.
 */
export interface SelectedSubtitle {
  url: string;
  lang: string;
  label: string;
  /**
   * OpenSubtitles file id, or null for a track extracted from the release
   * itself — those have no external id, and mpv finds them on its own.
   */
  file_id: number | null;
  forced?: boolean;
  /** Came from inside the release rather than from OpenSubtitles. */
  embedded?: boolean;
  /** true when this track was chosen by the auto-select rules, not the user. */
  auto?: boolean;
}

type Track = Awaited<ReturnType<typeof Api.subtitles.search>>["tracks"][number];

function toSelected(t: Track, auto = false): SelectedSubtitle {
  return {
    url: Api.subtitles.trackUrl(t.file_id) + `?token=${encodeURIComponent(localStorage.getItem("wallace-tv:bearer") ?? "")}`,
    lang: t.language,
    label: t.display_label,
    file_id: t.file_id,
    forced: t.forced,
    auto,
  };
}

export function SubtitlePicker({
  imdbId,
  kind,
  season,
  episode,
  selected,
  onSelect,
  originalLanguage,
  audioLanguage,
}: {
  imdbId: string | null;
  kind: "movie" | "series";
  season?: number;
  episode?: number;
  selected: SelectedSubtitle | null;
  onSelect: (s: SelectedSubtitle | null) => void;
  /** ISO original language (e.g. "en", "ko"). When set, the picker fetches
   *  eagerly so the auto-select rules can run before the menu is opened. */
  originalLanguage?: string | null;
  /**
   * Language of the audio track actually being played, when known.
   *
   * This, not the film's original language, is what decides whether full
   * subtitles are needed: the audio selector now picks English wherever a
   * release offers it, so a Japanese film watched with its English dub needs
   * no captions — while the same film with only Japanese audio needs them or
   * it's unwatchable.
   */
  audioLanguage?: string | null;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);

  // Auto-select needs the track list before the user opens the menu, so fetch
  // eagerly whenever we know the original language.
  const wantAuto = !!originalLanguage || !!audioLanguage;

  const q = useQuery({
    queryKey: ["subtitles", imdbId, kind, season, episode],
    queryFn: () => Api.subtitles.search({
      imdb_id: imdbId!,
      kind,
      season,
      episode,
      languages: "en",
    }),
    enabled: !!imdbId && (open || wantAuto),
    staleTime: 5 * 60_000,
  });

  // Run the auto-select rules once per item, only if the user hasn't picked.
  const autoAppliedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!wantAuto || !imdbId || selected) return;
    const tracks = q.data?.tracks;
    if (!tracks) return;
    const key = `${imdbId}|${season ?? ""}|${episode ?? ""}`;
    if (autoAppliedFor.current === key) return;
    autoAppliedFor.current = key;

    // Captions default to OFF. The single exception is a forced track, which
    // exists precisely to be shown unasked — it carries only the translated
    // signs and the lines of dialogue that aren't in the language you're
    // listening to, so it is never the intrusive full-transcript kind.
    const forced = tracks.find((t) => t.forced);
    if (forced) {
      onSelect(toSelected(forced, true));
      return;
    }

    // No forced track: still switch full subtitles on when the audio being
    // played isn't English, because then they are the only way to follow the
    // film rather than an unwanted overlay. Judged on the audio language, not
    // the film's original language — an English dub of a Japanese film needs
    // nothing, the Japanese original needs everything.
    const audio = (audioLanguage ?? "").toLowerCase().slice(0, 2);
    const audioIsEnglish = audio === "en" || audio === "";
    if (!audioIsEnglish) {
      const full = tracks.find((t) => t.language.startsWith("en") && !t.forced) ?? tracks[0];
      if (full) onSelect(toSelected(full, true));
    }
  }, [wantAuto, imdbId, season, episode, originalLanguage, audioLanguage, selected, q.data, onSelect]);

  if (!imdbId) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`pill-ghost text-sm ${selected ? "text-goldHi" : ""}`}
        title="Pick a subtitle track"
      >
        <Captions size={14} />
        {selected ? `Subs: ${selected.label}` : "Subtitles"}
        <ChevronDown size={12} className={open ? "rotate-180 transition" : "transition"} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-30 card bg-lacquer/95 backdrop-blur border border-hairline w-80 max-h-96 overflow-y-auto shadow-2xl">
          <div className="px-3 py-2 border-b border-hairline flex items-center justify-between">
            <div className="section-label">Subtitles · English</div>
            <button onClick={() => setOpen(false)} className="pill-ghost p-1" aria-label="Close">
              <X size={14} />
            </button>
          </div>

          <ul className="py-1">
            <li>
              <button
                onClick={() => { onSelect(null); setOpen(false); }}
                className={`w-full text-left px-3 py-2 hover:bg-elev/60 text-sm flex items-center gap-2 ${!selected ? "text-goldHi" : "text-parchment"}`}
              >
                {!selected && <Check size={14} />}
                <span className={!selected ? "" : "ml-5"}>Off (no subtitles)</span>
              </button>
            </li>

            {q.isLoading && (
              <li className="px-3 py-2 text-parchment text-xs">Searching…</li>
            )}
            {q.isError && (
              <li className="px-3 py-2 text-vermilionHi text-xs">{(q.error as Error).message}</li>
            )}
            {q.data?.tracks.length === 0 && (
              <li className="px-3 py-2 text-parchment/70 text-xs">No subtitles found for this item.</li>
            )}
            {q.data?.tracks.map((t) => {
              const isSelected = selected?.file_id === t.file_id;
              return (
                <li key={t.file_id}>
                  <button
                    onClick={() => {
                      onSelect(toSelected(t));
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 hover:bg-elev/60 text-sm group ${isSelected ? "text-goldHi" : "text-parchment hover:text-ivory"}`}
                  >
                    <div className="flex items-center gap-2">
                      {isSelected && <Check size={14} />}
                      <span className={isSelected ? "" : "ml-5"}>{t.display_label}</span>
                      {t.from_trusted && (
                        <span className="text-[10px] text-jade ml-auto font-display tracking-smallcaps">TRUSTED</span>
                      )}
                    </div>
                    {t.release && (
                      <div className="ml-5 text-parchment/60 text-[11px] truncate mt-0.5" title={t.release}>
                        {t.release}
                      </div>
                    )}
                    <div className="ml-5 text-parchment/50 text-[10px] mt-0.5">
                      {t.download_count.toLocaleString()} downloads
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Loads the forced subtitle track that came with the release.
 *
 * A release's own forced track beats anything from OpenSubtitles: it is
 * already timed to this exact encode, so there is no drift to correct. The
 * HLS pass writes it out as a WebVTT sidecar while it transcodes (it has to
 * demux the whole file anyway), and this watches for it to appear.
 *
 * The sidecar grows as the transcode advances, and a <track> is fetched once,
 * so the URL carries a coarse version stamp — bumped every two minutes of
 * extracted content rather than on every poll, which would re-fetch the file
 * constantly for no benefit. A caption may blink at the swap.
 *
 * Renders nothing: it exists to run a query and an effect where Watch itself
 * can't, since Watch returns early before this point.
 */
export function ForcedSubtitleLoader({
  streamUrl,
  audio,
  active,
  onLoad,
}: {
  streamUrl: string;
  audio: number | null;
  /** Whatever is currently showing — a deliberate choice is never overridden. */
  active: SelectedSubtitle | null;
  onLoad: (s: SelectedSubtitle) => void;
}): null {
  const sourceUrl = hlsSourceUrl(streamUrl);

  // Re-encode (v=h264) sessions are keyed separately server-side; carrying the
  // flag keeps this poll on the same key as TranscodeProgressChip so the two
  // share one query — and forced subs resolve during saver playback.
  const sessionParams = streamUrl.includes("/api/hls/session.m3u8")
    ? new URLSearchParams(streamUrl.split("?")[1] ?? "")
    : null;
  const videoMode = sessionParams?.get("v") ?? null;
  const startS = sessionParams?.get("start") ?? null;

  // Same query key as TranscodeProgressChip, so the two share one poll.
  const { data } = useQuery({
    queryKey: ["hls-status", sourceUrl, audio, videoMode, startS],
    queryFn: () => Api.hls.status(sourceUrl!, audio, videoMode, startS),
    enabled: Boolean(sourceUrl),
    refetchInterval: (q) => (q.state.data?.state === "complete" ? false : 4000),
  });

  const url = data?.forced_subtitle_url ?? null;
  const version = Math.floor((data?.cached_s ?? 0) / 120);

  useEffect(() => {
    if (!url) return;
    // The viewer's own pick outranks the automatic one.
    if (active && !active.embedded) return;
    const token = localStorage.getItem("wallace-tv:bearer") ?? "";
    const stamped = `${url}?token=${encodeURIComponent(token)}&v=${version}`;
    if (active?.url === stamped) return;
    onLoad({
      url: stamped,
      lang: "",
      label: "Forced (in release)",
      file_id: null,
      forced: true,
      auto: true,
      embedded: true,
    });
  }, [url, version, active, onLoad]);

  return null;
}
